import clsx from 'clsx';
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { BiChevronDown, BiUpArrowAlt } from 'react-icons/bi';
import { FiLock } from 'react-icons/fi';

import s from './Breadcrumb.module.scss';

const MAX_WIDTH = 340;
const MIN_HEIGHT = 120;

export interface BreadcrumbSibling {
  label: ReactNode;
  onClick: () => void;
  current?: boolean;
  icon?: ReactNode;
  /** Plan-locked for the owner — padlocked, and still opens the upgrade placeholder. */
  locked?: boolean;
}

interface BreadcrumbSwitcherProps {
  label: ReactNode;
  icon?: ReactNode;
  locked?: boolean;
  siblings: BreadcrumbSibling[];
  parent?: BreadcrumbSibling;
}

const LOCK_TITLE = 'Not on your plan';

export const BreadcrumbSwitcher = ({
  label,
  icon,
  locked,
  siblings,
  parent,
}: BreadcrumbSwitcherProps) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;

    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const spaceBelow = window.innerHeight - rect.bottom;
      const upwards = rect.top > spaceBelow;

      setMenuStyle({
        position: 'fixed',
        left: Math.max(8, Math.min(rect.left, window.innerWidth - MAX_WIDTH - 8)),
        width: Math.min(MAX_WIDTH, window.innerWidth - 16),
        zIndex: 10000,
        maxHeight: Math.max((upwards ? rect.top : spaceBelow) - 12, MIN_HEIGHT),
        ...(upwards ? { bottom: window.innerHeight - rect.top + 6 } : { top: rect.bottom + 6 }),
      });
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);

    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const moveFocus = (from: HTMLElement, delta: number) => {
    const rows = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('button') ?? []);
    const index = rows.indexOf(from);
    if (index === -1) return;
    rows[(index + delta + rows.length) % rows.length]?.focus();
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={s.trigger}
        aria-expanded={open}
        aria-haspopup="dialog"
        title={locked ? LOCK_TITLE : undefined}
        onClick={() => setOpen((value) => !value)}
      >
        {icon && <span className={s.icon}>{icon}</span>}
        <span className={s.label}>{label}</span>
        {locked && <FiLock className={s.lock} aria-label={LOCK_TITLE} />}
        <BiChevronDown className={clsx(s.chevron, open && s.chevronOpen)} aria-hidden="true" />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            className={s.menu}
            style={menuStyle}
            role="dialog"
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                moveFocus(event.target as HTMLElement, 1);
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault();
                moveFocus(event.target as HTMLElement, -1);
              }
            }}
          >
            {parent && (
              <div className={s.menuGroup}>
                <button
                  type="button"
                  className={clsx(s.menuRow, s.menuRowAscent)}
                  onClick={() => {
                    setOpen(false);
                    parent.onClick();
                  }}
                >
                  <span className={s.menuRowIcon} aria-hidden="true">
                    <BiUpArrowAlt />
                  </span>
                  <span className={s.menuRowLabel}>{parent.label}</span>
                </button>
              </div>
            )}
            <div className={clsx(s.menuGroup, parent && s.menuGroupDivided)}>
              {siblings.map((sibling, index) => (
                <button
                  key={index}
                  type="button"
                  className={clsx(
                    s.menuRow,
                    sibling.current && s.menuRowCurrent,
                    sibling.locked && s.menuRowLocked,
                  )}
                  aria-current={sibling.current ? 'page' : undefined}
                  title={sibling.locked ? LOCK_TITLE : undefined}
                  onClick={() => {
                    setOpen(false);
                    if (!sibling.current) sibling.onClick();
                  }}
                >
                  <span className={s.menuRowIcon} aria-hidden="true">
                    {sibling.current ? '▸' : sibling.icon}
                  </span>
                  <span className={s.menuRowLabel}>{sibling.label}</span>
                  {/* A row the plan withholds keeps its own icon and says so on the right,
                      rather than losing what it is to the padlock. */}
                  {sibling.locked && <FiLock className={s.menuRowLock} aria-label={LOCK_TITLE} />}
                </button>
              ))}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
